// 教学工作台路由：集中配置工作台、教学工具和数据页面入口。
import type { RouteRecordRaw } from "vue-router";
import { RouterView } from "vue-router";

const OfficeLayout = () => import("@/layout/OfficeLayout/index.vue");

const officeRoutes: RouteRecordRaw = {
  path: "/office",
  component: OfficeLayout,
  redirect: "/office/workbench",
  name: "Office",
  meta: {
    title: "教学工作台",
    icon: "workbench",
    hidden: true,
    permissionKey: "office",
  },
  children: [
    {
      path: "workbench",
      name: "OfficeWorkbench",
      component: () => import("@/views/office/workbench/index.vue"),
      meta: {
        title: "工作台",
        icon: "workbench",
        permissionKey: "office:workbench",
      },
    },
    {
      path: "class-management",
      name: "OfficeClassManagement",
      component: () => import("@/views/office/class-management/index.vue"),
      meta: {
        title: "班级管理",
        icon: "class",
        // 沿用现有班级权限标识，避免权限配置未同步时影响教师访问。
        permissionKey: "office:myClass",
      },
    },
    {
      path: "assign-practice",
      name: "OfficeAssign",
      component: RouterView,
      redirect: "/office/assign-practice/practice",
      meta: {
        title: "布置练习",
        icon: "assign-practice",
        permissionKey: "office:assignPractice",
      },
      children: [
        {
          path: "practice",
          name: "OfficeAssignPractice",
          component: () => import("@/views/office/assign-practice/index.vue"),
          meta: {
            title: "布置练习",
            permissionKey: "office:assignPractice",
          },
        },
        {
          path: "exam",
          name: "OfficeAssignExam",
          component: () => import("@/views/office/assign-exam/index.vue"),
          meta: {
            title: "布置考试",
            hidden: true,
            permissionKey: "office:assignExam",
          },
        },
      ],
    },
    {
      path: "word-pk/match/:wordPkProjectId",
      name: "OfficeWordPkMatch",
      component: () => import("@/views/office/word-pk/match.vue"),
      meta: {
        title: "单词PK",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:wordPk:match",
      },
    },
    {
      path: "sync-classroom",
      name: "OfficeSyncClassroom",
      component: () => import("@/views/office/sync-classroom/entry.vue"),
      meta: {
        title: "同步练习",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        // 独立权限尚未接入，先复用工作台权限保证教师可从卡片进入。
        permissionKey: "office:workbench",
      },
    },
    {
      path: "sync-classroom/practice",
      name: "OfficeSyncClassroomPractice",
      component: () => import("@/views/office/sync-classroom/index.vue"),
      meta: {
        title: "同步练习",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:workbench",
      },
    },
    {
      path: "sync-classroom/book-select",
      name: "OfficeSyncClassroomBookSelect",
      component: () => import("@/views/office/sync-classroom/book-select.vue"),
      meta: {
        title: "选择教材",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:workbench",
      },
    },
    {
      path: "extra-reading",
      name: "OfficeExtraReading",
      component: () => import("@/views/office/sync-classroom/entry.vue"),
      meta: {
        title: "课外拓展",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncClassroomMode: "extra-reading",
        permissionKey: "office:workbench",
      },
    },
    {
      path: "extra-reading/practice",
      name: "OfficeExtraReadingPractice",
      component: () => import("@/views/office/sync-classroom/index.vue"),
      meta: {
        title: "课外拓展",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncClassroomMode: "extra-reading",
        permissionKey: "office:workbench",
      },
    },
    {
      path: "extra-reading/book-select",
      name: "OfficeExtraReadingBookSelect",
      component: () => import("@/views/office/sync-classroom/book-select.vue"),
      meta: {
        title: "选择课外内容",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncClassroomMode: "extra-reading",
        permissionKey: "office:workbench",
      },
    },
    {
      path: "word-pk/settlement/:wordPkProjectId",
      name: "OfficeWordPkSettlement",
      component: () => import("@/views/office/word-pk/settlement.vue"),
      meta: {
        title: "单词PK结算",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:wordPk:settlement",
      },
    },
    {
      path: "word-pk",
      name: "OfficeWordPk",
      component: () => import("@/views/office/word-pk/index.vue"),
      meta: {
        title: "单词PK",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:wordPk",
      },
    },
    {
      path: "word-dictation",
      name: "OfficeWordDictation",
      component: () => import("@/views/office/sync-classroom/book-select.vue"),
      meta: {
        title: "单词听写",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncClassroomMode: "word-dictation",
        // 独立权限尚未接入，先复用工作台权限保证教师可从卡片进入。
        permissionKey: "office:workbench",
      },
    },
    {
      path: "word-dictation/practice",
      name: "OfficeWordDictationPractice",
      component: () => import("@/views/office/word-dictation/index.vue"),
      meta: {
        title: "单词听写",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncClassroomMode: "word-dictation",
        permissionKey: "office:workbench",
      },
    },
    {
      path: "word-dictation/session",
      name: "OfficeWordDictationSession",
      component: () => import("@/views/office/word-dictation/session.vue"),
      meta: {
        title: "单词听写",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncClassroomMode: "word-dictation",
        permissionKey: "office:workbench",
      },
    },
    {
      path: "word-reading/reading/:wordReadingProjectId",
      name: "OfficeWordReadingSession",
      component: () => import("@/views/office/word-reading/reading.vue"),
      meta: {
        title: "单词跟读",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:wordReading:reading",
      },
    },
    {
      path: "word-reading",
      name: "OfficeWordReading",
      component: () => import("@/views/office/word-reading/index.vue"),
      meta: {
        title: "单词跟读",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        permissionKey: "office:wordReading",
      },
    },
    {
      path: "soundmark",
      name: "OfficeSoundmark",
      component: () => import("@/views/office/soundmark/index.vue"),
      meta: {
        title: "音标学习",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        syncOfficeHorizontalScroll: true,
        permissionKey: "office:soundmark",
      },
    },
    {
      path: "writing-correction",
      name: "OfficeWritingCorrection",
      component: () => import("@/views/office/writing-correction/index.vue"),
      meta: {
        title: "AI作文批改",
        hideOfficeSidebar: true,
        hidden: true,
        isFullScreen: true,
        // 独立权限尚未接入，先复用工作台权限保证教师可从卡片进入。
        permissionKey: "office:workbench",
      },
    },
    {
      path: "practice-review",
      name: "OfficePracticeReview",
      component: () => import("@/views/office/practice-review/index.vue"),
      meta: {
        title: "练习讲评",
        icon: "practice-review",
        permissionKey: "office:practiceReview",
      },
    },
    {
      path: "practice-review/detail/:examProjectId",
      name: "OfficePracticeReviewDetail",
      component: () => import("@/views/office/practice-review/detail.vue"),
      meta: {
        title: "查看练习详情",
        hidden: true,
        permissionKey: "office:practiceReview",
      },
    },
    // 无 testPaperId 或 exportPaper 不可用时回退：课堂端旧讲评统计页
    {
      path: "practice-review/legacy-stats",
      name: "OfficePracticeReviewLegacyStats",
      component: () =>
        import("@/views/office/practice-review/legacy-comment/index.vue"),
      meta: {
        title: "练习讲评统计",
        hidden: true,
        permissionKey: "office:practiceReview",
      },
    },
    // 无 testPaperId 或 exportPaper 不可用时回退：课堂端旧讲评三栏展示
    {
      path: "practice-review/legacy-review",
      name: "OfficePracticeReviewLegacyReview",
      component: () =>
        import("@/views/office/practice-review/legacy-comment/comment-review.vue"),
      meta: {
        title: "练习讲评展示",
        hidden: true,
        permissionKey: "office:practiceReview",
        // 讲评展示需要更大内容区
        hideOfficeSidebar: true,
      },
    },
    {
      path: "practice-score",
      name: "OfficePracticeScore",
      component: () => import("@/views/office/data-analysis/list.vue"),
      meta: {
        title: "数据分析",
        icon: "data-analysis",
        permissionKey: "office:practiceScore",
      },
    },
    {
      path: "practice-score/detail/:examProjectId",
      name: "OfficePracticeScoreDetail",
      component: () => import("@/views/office/data-analysis/detail.vue"),
      meta: {
        title: "练习数据分析详情",
        hidden: true,
        permissionKey: "office:practiceScore",
      },
    },
    {
      path: "practice-score/essay-detail/:studentId",
      name: "OfficeStudentEssayDetail",
      component: () => import("@/views/office/data-analysis/essay-detail.vue"),
      meta: {
        title: "查看学生作文详情",
        hidden: true,
        permissionKey: "office:practiceScore",
      },
    },
    {
      path: "practice-score/essay-report/:studentId",
      name: "OfficeStudentEssayReport",
      component: () => import("@/views/office/data-analysis/essay-report.vue"),
      meta: {
        title: "学生作文报告",
        // 独立报告页：无顶栏/侧栏，便于阅读与导出
        hideOfficeHeader: true,
        hideOfficeSidebar: true,
        hidden: true,
        permissionKey: "office:practiceScore",
      },
    },
    {
      path: "learning-tracking",
      name: "OfficeLearningTracking",
      component: RouterView,
      redirect: "/office/learning-tracking/progress-detail",
      meta: {
        title: "学情追踪",
        icon: "learning-tracking",
        permissionKey: "office:learningTracking",
        alwaysShow: true,
      },
      children: [
        {
          path: "progress-detail",
          name: "OfficeLearningTrackingProgressDetail",
          component: () =>
            import("@/views/office/learning-tracking/progress-detail.vue"),
          meta: {
            title: "进退步详情",
            permissionKey: "office:learningTracking",
          },
        },
        {
          path: "excellent-student-detail",
          name: "OfficeLearningTrackingExcellentStudentDetail",
          component: () =>
            import("@/views/office/learning-tracking/excellent-student-detail.vue"),
          meta: {
            title: "优秀学生详情",
            permissionKey: "office:learningTracking",
          },
        },
        {
          path: "student-detail",
          name: "OfficeLearningTrackingStudentDetail",
          component: () =>
            import("@/views/office/learning-tracking/student-detail.vue"),
          meta: {
            title: "学生详情",
            permissionKey: "office:learningTracking:studentDetail",
          },
        },
        {
          // 单生画像：从列表「查看详情」进入，path 带考号
          path: "student-detail/profile/:examAccount",
          name: "OfficeLearningTrackingStudentProfile",
          component: () =>
            import("@/views/office/learning-tracking/student-profile.vue"),
          meta: {
            title: "学生详情",
            hidden: true,
            permissionKey: "office:learningTracking:studentDetail",
          },
        },
        {
          path: "class-detail",
          name: "OfficeLearningTrackingClassDetail",
          component: () =>
            import("@/views/office/learning-tracking/class-detail.vue"),
          meta: {
            title: "班级详情",
            permissionKey: "office:learningTracking",
          },
        },
      ],
    },
    {
      path: "profile",
      name: "OfficeProfile",
      component: () => import("@/views/office/profile/index.vue"),
      meta: {
        title: "个人中心",
        icon: "personal-center",
        permissionKey: "office:profile",
      },
    },
  ],
};

export default officeRoutes;
